// src/components/XiangqiBoard.tsx
import React, { useMemo } from "react";
import { Card } from "antd";

interface XiangqiBoardProps {
  fen: string;
}

// Constants
const ROWS = 10;
const COLS = 9;
const CELL_SIZE = 44; // px
const HALF_CELL = CELL_SIZE / 2;
const BOARD_WIDTH = COLS * CELL_SIZE;
const BOARD_HEIGHT = ROWS * CELL_SIZE;

// --- UTILS ---
const getPieceImage = (char: string): string | null => {
  // NOTE: Ensure these images exist in your public folder
  const baseUrl = "/pieces";
  const pieceMap: Record<string, string> = {
    K: "rK.svg",
    A: "rA.svg",
    B: "rB.svg",
    N: "rN.svg",
    R: "rR.svg",
    C: "rC.svg",
    P: "rP.svg",
    k: "bK.svg",
    a: "bA.svg",
    b: "bB.svg",
    n: "bN.svg",
    r: "bR.svg",
    c: "bC.svg",
    p: "bP.svg",
  };
  const fileName = pieceMap[char];
  return fileName ? `${baseUrl}/${fileName}` : null;
};

// --- SUB-COMPONENT: BOARD GRID (SVG) ---
// This draws the lines, the river gap, and the palace diagonals efficiently.
const BoardGrid: React.FC = () => {
  const lineColor = "#6b4423";
  const start = HALF_CELL;
  const endX = BOARD_WIDTH - HALF_CELL;
  const endY = BOARD_HEIGHT - HALF_CELL;

  // Palace Coordinates
  // Top Palace: (3,0) to (5,2) -> x: index*44+22
  const pStart = start + 3 * CELL_SIZE; // Col 3
  const pEnd = start + 5 * CELL_SIZE; // Col 5
  const pTopBase = start; // Row 0
  const pTopEnd = start + 2 * CELL_SIZE; // Row 2

  const pBotBase = endY; // Row 9
  const pBotTop = endY - 2 * CELL_SIZE; // Row 7

  return (
    <svg
      width={BOARD_WIDTH}
      height={BOARD_HEIGHT}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      {/* 1. Horizontal Lines (10 lines) */}
      {Array.from({ length: ROWS }).map((_, i) => (
        <line
          key={`h-${i}`}
          x1={start}
          y1={start + i * CELL_SIZE}
          x2={endX}
          y2={start + i * CELL_SIZE}
          stroke={lineColor}
          strokeWidth={2}
        />
      ))}

      {/* 2. Vertical Lines (Outer borders run full length, Inner stop at river) */}
      {Array.from({ length: COLS }).map((_, i) => {
        const x = start + i * CELL_SIZE;
        // Outer lines (0 and 8)
        if (i === 0 || i === COLS - 1) {
          return (
            <line
              key={`v-${i}`}
              x1={x}
              y1={start}
              x2={x}
              y2={endY}
              stroke={lineColor}
              strokeWidth={2}
            />
          );
        }
        // Inner lines (Top half)
        const topRiverY = start + 4 * CELL_SIZE;
        const botRiverY = start + 5 * CELL_SIZE;
        return (
          <React.Fragment key={`v-${i}`}>
            <line
              x1={x}
              y1={start}
              x2={x}
              y2={topRiverY}
              stroke={lineColor}
              strokeWidth={2}
            />
            <line
              x1={x}
              y1={botRiverY}
              x2={x}
              y2={endY}
              stroke={lineColor}
              strokeWidth={2}
            />
          </React.Fragment>
        );
      })}

      {/* 3. Palace Diagonals (Top) */}
      <line
        x1={pStart}
        y1={pTopBase}
        x2={pEnd}
        y2={pTopEnd}
        stroke={lineColor}
        strokeWidth={2}
      />
      <line
        x1={pEnd}
        y1={pTopBase}
        x2={pStart}
        y2={pTopEnd}
        stroke={lineColor}
        strokeWidth={2}
      />

      {/* 4. Palace Diagonals (Bottom) */}
      <line
        x1={pStart}
        y1={pBotBase}
        x2={pEnd}
        y2={pBotTop}
        stroke={lineColor}
        strokeWidth={2}
      />
      <line
        x1={pEnd}
        y1={pBotBase}
        x2={pStart}
        y2={pBotTop}
        stroke={lineColor}
        strokeWidth={2}
      />
    </svg>
  );
};

// --- MAIN COMPONENT ---
const XiangqiBoard: React.FC<XiangqiBoardProps> = ({ fen }) => {
  const boardMatrix = useMemo(() => {
    if (!fen) return [];
    const rows = fen.split(" ")[0].split("/");
    const matrix: string[][] = [];
    rows.forEach((row) => {
      const matrixRow: string[] = [];
      for (const char of row) {
        if (!isNaN(Number(char))) {
          for (let i = 0; i < Number(char); i++) matrixRow.push("");
        } else {
          matrixRow.push(char);
        }
      }
      matrix.push(matrixRow);
    });
    return matrix;
  }, [fen]);

  if (boardMatrix.length === 0) return null;

  return (
    <Card
      bordered={false}
      style={{
        background: "#e6cca5",
        borderRadius: 16,
        width: "fit-content",
        margin: "0 auto",
        boxShadow:
          "0 20px 50px rgba(0,0,0,0.15), inset 0 0 60px rgba(140, 94, 60, 0.3)",
        border: "12px solid #8c5e3c",
        position: "relative",
      }}
      bodyStyle={{ padding: 15 }} // Padding creates the space around the grid
    >
      <div
        style={{
          position: "relative",
          width: BOARD_WIDTH,
          height: BOARD_HEIGHT,
          userSelect: "none",
        }}
      >
        {/* === LAYER 1: BOARD LINES (SVG) === */}
        <BoardGrid />

        {/* === LAYER 2: RIVER TEXT === */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            width: "100%",
            transform: "translateY(-50%)",
            display: "flex",
            justifyContent: "space-around",
            padding: "0 40px",
            color: "#6b4423",
            opacity: 0.8,
            fontSize: "20px",
            fontFamily: '"Lexend Deca", "Noto Serif SC", serif',
            fontWeight: "bold",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <span>楚 河</span>
          <span>漢 界</span>
        </div>

        {/* === LAYER 3: PIECES (GRID) === */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${COLS}, ${CELL_SIZE}px)`,
            gridTemplateRows: `repeat(${ROWS}, ${CELL_SIZE}px)`,
            position: "relative",
            zIndex: 10,
          }}
        >
          {boardMatrix.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const imgUrl = getPieceImage(cell);

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{
                    width: CELL_SIZE,
                    height: CELL_SIZE,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {imgUrl && (
                    <img
                      src={imgUrl}
                      alt={cell}
                      crossOrigin="anonymous"
                      style={{
                        width: CELL_SIZE - 4, // 40px
                        height: CELL_SIZE - 4,
                        filter: "drop-shadow(2px 4px 3px rgba(0,0,0,0.3))",
                        transform: "translateY(-2px)",
                        transition:
                          "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        cursor: "grab",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform =
                          "translateY(-6px) scale(1.1)";
                        e.currentTarget.style.filter =
                          "drop-shadow(4px 10px 6px rgba(0,0,0,0.4))";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.filter =
                          "drop-shadow(2px 4px 3px rgba(0,0,0,0.3))";
                      }}
                    />
                  )}
                </div>
              );
            }),
          )}
        </div>
      </div>
    </Card>
  );
};

export default XiangqiBoard;
